import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useGA, useIsValidWETHNetwork } from '~/hooks'
import { useModalOpened, useModalToggle } from '~/state/application/hooks'
import IconSwapArrow from '~/assets/images/swap-arrow.svg'
import { ApplicationModal } from '~/state/application/reducer'
import Footer from '../Footer'
import Header from '../Header'
import { ArrowButton } from '../LimitOrder/components/LimitSwap/styled'
import { TradeDirection } from '../Exchange/types'
import TradeForm from '../TradeForm'
import { StyledWethStatement } from './styled'
import { Review } from './components/Review'
import { Result } from './components/Result'
import { toggleDirection, updateValue, useWETHContext } from './context'
import { ethers } from 'ethers'
import { useInputAmountVerify, useIsDisableTrade } from '~/state/dex/hooks'
import { message } from '../Message'
import BigNumber from 'bignumber.js'
import { LOCALES } from '~/locales'
import { Box, Page } from '../Layout'
import { useWETHRouter, useSpotSwapRouter, useLimitOrderRouter } from '~/hooks'
import { useWeb3React } from '@web3-react/core'
import { formatFormAmount } from '~/utils/numbers'
import Space from '~/components/Space'
import NotificationBar from '~/components/NotificationBar'

const WETHStatement = () => {
  const { i18n } = useTranslation()
  const { trackElClick } = useGA()
  const lang = i18n.resolvedLanguage === LOCALES.ZH ? 'zh-cn' : 'en-us'
  const link = `https://support.tokenlon.im/hc/${lang}/articles/10471967656212`
  return (
    <StyledWethStatement>
      <Trans i18nKey="weth_statement">
        Learn more about
        <a
          href={link}
          target="_blank"
          data-trackid="webtl_weth_more"
          data-trackdata={JSON.stringify({ url: link })}
          onClick={trackElClick}
          rel="noopener noreferrer"
        >
          WETH
        </a>
      </Trans>
    </StyledWethStatement>
  )
}

const WrapForm = () => {
  const { trackClick } = useGA()
  const isWETHRouter = useWETHRouter()
  const isSpotSwapRouter = useSpotSwapRouter()
  const isLimitOrder = useLimitOrderRouter()
  const isWithChart = isSpotSwapRouter || isLimitOrder

  const { account } = useWeb3React()
  const { state, dispatch } = useWETHContext()
  const { isWrap, inputToken, outputToken, gasInfo, balance } = state
  const [_value, setValue] = useState('')
  const { t } = useTranslation()
  const title = isWrap ? t('wrap_eth') : t('unwrap_eth')

  const isValidNetwork = useIsValidWETHNetwork()
  const isDisableTrade = useIsDisableTrade()

  const btnText = account
    ? isValidNetwork
      ? t('preview_order')
      : t('switch_network')
    : t('connect')

  const inputVerify = useInputAmountVerify()
  const balanceFormatted = balance?.toAutoFormat(18)
  const isOpen = useModalOpened(ApplicationModal.WETH_WRAP)

  const toggleWalletModal = useModalToggle(ApplicationModal.WALLET_CONNECT, {
    stack: true,
  })
  const toggleSwitchNetworkModal = useModalToggle(
    ApplicationModal.SWITCH_NETWORK,
    { stack: true },
  )
  const togglePreviewModal = useModalToggle(ApplicationModal.WETH_PREVIEW)
  const toggleFormModal = useModalToggle(ApplicationModal.WETH_WRAP)
  const toggleIpBlockedModal = useModalToggle(ApplicationModal.IP_BLOCKED, {
    stack: true,
  })

  const onDirectionChange = () => {
    dispatch(toggleDirection())
  }

  const onConfirm = () => {
    if (!account) {
      trackClick('webtl_walletconnect', {
        from: isWrap ? 'wrap_weth' : 'unwrap_weth',
      })
      toggleWalletModal()
      return
    }
    if (!isValidNetwork) {
      toggleSwitchNetworkModal()
      return
    }
    if (isDisableTrade) {
      toggleIpBlockedModal()
      return
    }
    if (!inputVerify(_value) || new BigNumber(0).gte(_value)) {
      message.error(t('unvalid_input_amount'))
      return
    }

    if (balance.lt(ethers.utils.parseEther(_value).toString())) {
      message.error(
        t('$symbol_insufficient_balance', { symbol: inputToken.symbol }),
      )
      return
    }

    dispatch(updateValue(_value))
    trackClick('webtl_weth_preview', {
      taker_token_symbol: outputToken.symbol,
      maker_token_symbol: inputToken.symbol,
      taker_token_amount: _value,
      maker_token_amount: _value,
    })
    togglePreviewModal()
  }
  const onValueChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const input = e.target.value

    // delete all non-number
    if (input === '') return setValue('')

    try {
      const [int, digit] = input.split('.')
      if (digit) {
        if (digit.includes('.')) return
        if (digit.length > 6) return
      }
    } catch (error) {
      console.error(error)
    }

    // with input
    if (inputVerify(input)) {
      setValue(input)
    }
  }

  const onInputFocus = (e) => {
    const input = e.target.value
    const amount = new BigNumber(input).decimalPlaces(6).toString()
    setValue(amount)
  }

  const onMax = () => {
    trackClick('webtl_weth_max')
    if (!balanceFormatted || !balanceFormatted.toString()) return setValue('0')
    if (inputToken && inputToken.symbol === 'WETH')
      return setValue(new BigNumber(balanceFormatted.toString()).toFixed())

    const max = new BigNumber(balanceFormatted.toString()).minus(gasInfo.ether)

    setValue(max.gt(0) ? max.toFixed() : '0')
  }
  const onBack = () => {
    dispatch(updateValue(''))
    toggleFormModal()
  }

  useEffect(() => {
    return () => {
      dispatch(updateValue(''))
    }
  }, [])

  useEffect(() => {
    setValue(state.value)
  }, [state.value])

  return (
    <TradeForm
      title={title}
      mode={'overlay'}
      icon={isWETHRouter ? null : 'back'}
      visible={isOpen}
      nest
      onBack={onBack}
      className={`${isWithChart && 'withChart'} ${
        isSpotSwapRouter && 'spotswap'
      }`}
    >
      <TradeForm.BalanceLabel
        showMax
        onMaxClick={onMax}
        inputToken={inputToken}
        balance={balanceFormatted?.toString() || '0'}
        gasFeeEther={gasInfo.ether}
      />
      <TradeForm.Input
        label={t('order_sell')}
        onChange={onValueChange}
        onFocus={onInputFocus}
        value={_value}
        placeholder={t('amount_for_sell')}
        direction={TradeDirection.Input}
        token={inputToken}
      />
      <ArrowButton onClick={onDirectionChange}>
        <img src={IconSwapArrow} />
      </ArrowButton>
      <Box mt="-3px">
        <TradeForm.Input
          label={t('order_receive')}
          onChange={onValueChange}
          onFocus={onInputFocus}
          value={_value}
          placeholder={t('amount_for_buy')}
          direction={TradeDirection.Output}
          token={outputToken}
        />
      </Box>
      <TradeForm.Divider />
      <TradeForm.Stack>
        <TradeForm.TipRow title={t('rate')}>
          1 {inputToken?.symbol} = 1 {outputToken?.symbol}
        </TradeForm.TipRow>
        <TradeForm.TipRow title={t('gas_fee')}>
          ${gasInfo.useValue}({formatFormAmount(gasInfo.ether, 3)} ETH)
        </TradeForm.TipRow>
        <TradeForm.TipRow title={t('trading_fee')}>$0(0)</TradeForm.TipRow>
        <TradeForm.TipRow title={t('network')}>
          <TradeForm.Network />
        </TradeForm.TipRow>
      </TradeForm.Stack>
      {!isValidNetwork && (
        <>
          <Space height={15} />
          <NotificationBar
            level="warning"
            text={t('unmatched_network_swap_warning')}
            showLink={false}
            showClose={false}
          />
        </>
      )}
      <TradeForm.Button onClick={onConfirm}>{btnText}</TradeForm.Button>
      <WETHStatement />
    </TradeForm>
  )
}

export const WrapETHForm = () => {
  return (
    <>
      <WrapForm />
      <Review />
      <Result />
    </>
  )
}

export default () => {
  return (
    <Page>
      <Header />
      <WrapETHForm />
      <Footer />
    </Page>
  )
}
